// ============================================================================
// CHANGO V5.0 — Resubmit License Page (§9.6.2d 阶段 4：重新提交资料)
// ============================================================================
// 客户被 mismatch_frozen 后只能进这页
// 顶部 banner 显示 audit_decision_note（管理员手写理由）
// 3 必填项红 `*`：新营业执照 / 手机号 / 短信验证码（scope='resubmit_license'）
// 提交调 POST /api/workspace/resubmit-license
// 成功 → 跳 /app/dashboard（audit_flag 回 pending_audit）
// ============================================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import SmsCodeInput from '../../components/SmsCodeInput.jsx';

const schema = z.object({
  license_file_url: z.string().min(1, '请上传营业执照'),
  phone: z.string().regex(/^1[3-9]\d{9}$/, '请输入中国大陆手机号'),
  sms_code: z.string().regex(/^\d{6}$/, '短信验证码是 6 位数字')
});

export default function ResubmitLicense() {
  const navigate = useNavigate();
  const { workspace, user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedLicenseUrl, setUploadedLicenseUrl] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      license_file_url: '',
      phone: user?.phone || '',
      sms_code: ''
    }
  });

  const phoneValue = watch('phone');

  // 营业执照上传
  const handleLicenseUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setServerError('营业执照文件大小不能超过 5MB');
      return;
    }

    setUploadingFile(true);
    setServerError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.upload('/api/upload/license', formData);
      const licenseUrl = res.url || res.license_file_url;
      if (!licenseUrl) throw new Error('上传接口未返回执照 URL');
      setUploadedLicenseUrl(licenseUrl);
      setValue('license_file_url', licenseUrl, { shouldValidate: true });
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : '上传失败';
      setServerError(msg);
    } finally {
      setUploadingFile(false);
    }
  };

  // 发送短信验证码
  const handleSendSmsCode = async ({ phone, scope }) => {
    const res = await api.post('/api/sms/send-code', { phone, scope });
    return res;
  };

  const onSubmit = async (values) => {
    setServerError(null);
    setSubmitting(true);
    try {
      await api.post('/api/workspace/resubmit-license', {
        license_file_url: values.license_file_url,
        phone: values.phone,
        sms_code: values.sms_code
      });
      // 重新提交资料成功跳 /app/dashboard
      navigate('/app/dashboard');
    } catch (err) {
      if (err instanceof HttpError) {
        setServerError(err.message || '提交失败');
      } else {
        setServerError(err?.message || '网络错误，请重试');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="mx-auto w-full max-w-2xl px-4">
        {/* 冻结警告 Banner */}
        {workspace?.registration_status === 'frozen_for_audit' && workspace?.audit_decision_note && (
          <div className="mb-8 rounded-lg border-l-4 border-red-500 bg-red-50 p-4">
            <h3 className="mb-2 font-semibold text-red-800">账户已被冻结</h3>
            <p className="mb-3 text-sm text-red-700">
              您的营业执照与公司信息核对不符，请重新上传。
            </p>
            <p className="rounded bg-white p-3 text-sm text-slate-700 border border-red-200">
              <span className="font-medium text-red-700">管理员备注：</span> {workspace.audit_decision_note}
            </p>
          </div>
        )}

        <h1 className="mb-2 text-2xl font-semibold text-slate-800">重新提交资料</h1>
        <p className="mb-8 text-sm text-slate-500">
          请重新上传营业执照并完成短信验证，以便我们重新审核。
        </p>

        {serverError ? (
          <div role="alert" className="alert-error mb-6">{serverError}</div>
        ) : null}

        <form noValidate onSubmit={handleSubmit(onSubmit)} className="card space-y-6 p-8">
          {/* 营业执照上传 */}
          <div>
            <label className="label">
              <span className="text-red-600">*</span> 营业执照
            </label>
            <p className="text-sm text-slate-500 mb-3">
              PNG/JPG 格式，单文件 ≤ 5MB。请上传清晰的营业执照照片。
            </p>
            <div className="rounded border-2 border-dashed border-slate-300 p-4">
              {uploadedLicenseUrl ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-slate-700">已上传新营业执照</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadedLicenseUrl('');
                      setValue('license_file_url', '', { shouldValidate: true });
                    }}
                    className="text-sm text-slate-500 hover:text-slate-700"
                  >
                    重新上传
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 cursor-pointer text-slate-600 hover:text-slate-800">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="text-sm">
                    {uploadingFile ? '上传中…' : '点击或拖拽上传营业执照'}
                  </span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={handleLicenseUpload}
                    disabled={uploadingFile}
                    className="hidden"
                  />
                </label>
              )}
            </div>
            {errors.license_file_url ? <p className="err">{errors.license_file_url.message}</p> : null}
          </div>

          {/* 手机号 + 短信验证码 */}
          <SmsCodeInput
            phone={phoneValue}
            phoneError={errors.phone?.message}
            codeValue={watch('sms_code')}
            codeError={errors.sms_code?.message}
            onPhoneChange={(val) => setValue('phone', val, { shouldValidate: true })}
            onCodeChange={(val) => setValue('sms_code', val, { shouldValidate: true })}
            onSendCode={handleSendSmsCode}
            scope="resubmit_license"
            disabled={false}
          />

          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => navigate('/app/account')}
              className="text-sm text-chango-600 hover:underline"
            >
              返回个人账号
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? <span className="spinner" aria-hidden="true" /> : null}
              {submitting ? '提交中…' : '提交审核'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
